/**
 * Core Tracker - Orquestra Visitor, Session e PageView (Fase 1).
 * Inicializa o Core Engine e envia PAGE_VIEW imediatamente (fora do batch).
 * Qualquer interação do usuário atualiza lastActivityAt (touchSession).
 */

import type { GomarkePixelConfig, PageViewRecord } from '../types';
import { getOrCreateVisitor } from './visitor';
import { getOrCreateSession, touchSession } from './session';
import {
  setPageViewCallback,
  clearPageViewCallback,
  trackPageView as trackPageViewCore,
  getCurrentPageViewState,
} from './pageview';
import {
  sendPageViewImmediately,
  startPeriodicFlush,
  stopPeriodicFlush,
} from './queue';
import { setupUnifiedUnloadFlush } from './unload';
import { getFingerprint } from '../utils/getFingerprint';
import { syncHcidToUrl } from '../utils/UrlSessionSync';
import type { CoreSession } from '../types';
import type { Visitor } from '../types';

export interface CoreTrackerConfig {
  config: GomarkePixelConfig;
  version?: string;
  /** Incluir fingerprint no visitor */
  withFingerprint?: boolean;
  /** Nova sessão quando domínio mudar */
  newSessionOnDomainChange?: boolean;
}

let currentVisitor: Visitor | null = null;
let currentSession: CoreSession | null = null;
let trackerConfig: CoreTrackerConfig | null = null;

/** Throttle para activity listener: atualiza lastActivityAt no máx. a cada 60s por interação. */
const ACTIVITY_THROTTLE_MS = 60 * 1000;
let lastActivityTouch = 0;
let activityListenersAttached = false;

function getVersion(): string {
  return trackerConfig?.config?.version ?? trackerConfig?.version ?? '1.0.0';
}

function touchSessionOnActivity(): void {
  const now = Date.now();
  if (now - lastActivityTouch < ACTIVITY_THROTTLE_MS) return;
  lastActivityTouch = now;
  if (currentSession) {
    currentSession = touchSession(currentSession);
  }
}

function startSessionActivityListener(): void {
  if (typeof window === 'undefined' || activityListenersAttached) return;
  activityListenersAttached = true;
  const events = ['scroll', 'click', 'keydown', 'mousemove', 'touchstart'];
  events.forEach(ev => {
    window.addEventListener(ev, touchSessionOnActivity);
  });
  window.addEventListener('focus', touchSessionOnActivity);
}

function stopSessionActivityListener(): void {
  if (typeof window === 'undefined' || !activityListenersAttached) return;
  activityListenersAttached = false;
  const events = ['scroll', 'click', 'keydown', 'mousemove', 'touchstart'];
  events.forEach(ev => {
    window.removeEventListener(ev, touchSessionOnActivity);
  });
  window.removeEventListener('focus', touchSessionOnActivity);
}

/** Evita PAGE_VIEW duplicado para a mesma URL em rajada (ex.: init + callback). */
const PAGE_VIEW_DEDUPE_MS = 2000;
let lastPageViewUrl = '';
let lastPageViewSentAt = 0;

function sendPageViewToApi(record: PageViewRecord): void {
  const cfg = trackerConfig?.config;
  const session = currentSession;
  const visitor = currentVisitor;
  if (!cfg || !session || !visitor) return;

  const now = Date.now();
  if (
    record.url === lastPageViewUrl &&
    now - lastPageViewSentAt < PAGE_VIEW_DEDUPE_MS
  ) {
    return;
  }
  lastPageViewUrl = record.url;
  lastPageViewSentAt = now;

  void sendPageViewImmediately({
    category: 'ENGAGEMENT',
    type: 'PAGE_VIEW',
    config: cfg,
    sessionId: session.sessionId,
    visitorId: visitor.visitorId,
    version: getVersion(),
    eventData: {
      page_url: record.url,
      page_title: record.title,
      path: record.path,
      referrer: record.referrer,
      hostname: typeof window !== 'undefined' ? window.location.hostname : '',
      max_scroll_percent: record.maxScrollPercent,
      duration_ms: record.durationMs,
      timestamp: record.timestamp,
    },
  });
}

/**
 * Inicializa o Core Engine: Visitor + Session + PageView (com interceptação SPA).
 * Deve ser chamado uma vez no carregamento do pixel.
 */
export async function initCoreTracker(options: CoreTrackerConfig): Promise<{
  visitor: Visitor;
  session: CoreSession;
}> {
  trackerConfig = options;
  const {
    config,
    withFingerprint = false,
    newSessionOnDomainChange = false,
  } = options;
  const debug = config.debug ?? false;

  const visitor = await getOrCreateVisitor({ withFingerprint, debug });
  currentVisitor = visitor;

  // Sessão sempre vinculada ao visitor (getOrCreateSession exige visitorId).
  const session = getOrCreateSession({
    visitorId: visitor.visitorId,
    newSessionOnDomainChange,
    debug,
  });
  currentSession = session;

  syncHcidToUrl(session.sessionId);

  setPageViewCallback(record => sendPageViewToApi(record));

  // Preload fingerprint para que getFingerprintSync() no unload já tenha cache.
  getFingerprint().catch(() => {});

  // Primeiro page view é imediato; o core só armazena estado, o disparo fica aqui.
  const initialState = getCurrentPageViewState();
  const initialRecord: PageViewRecord = {
    url: initialState.url,
    path: initialState.path,
    title: initialState.title,
    referrer: initialState.referrer,
    maxScrollPercent: initialState.maxScrollPercent,
    durationMs: 0,
    timestamp: Date.now(),
  };
  sendPageViewToApi(initialRecord);

  startPeriodicFlush(5000);
  setupUnifiedUnloadFlush();

  startSessionActivityListener();

  if (debug) {
    console.log('[AgTracker] Core inicializado', {
      visitorId: visitor.visitorId,
      sessionId: session.sessionId,
    });
  }

  return { visitor, session };
}

/**
 * Para o Core Engine e remove listener de pageview.
 */
export function destroyCoreTracker(): void {
  stopSessionActivityListener();
  stopPeriodicFlush();
  clearPageViewCallback();
  currentVisitor = null;
  currentSession = null;
  trackerConfig = null;
}

/**
 * Registro manual de uma visualização de página (ex.: carga inicial server-side).
 */
export function trackPageView(
  overrides?: Partial<PageViewRecord>
): PageViewRecord {
  return trackPageViewCore(overrides);
}

/**
 * Retorna o visitor atual (null se initCoreTracker ainda não foi chamado).
 */
export function getVisitor(): Visitor | null {
  return currentVisitor;
}

/**
 * Retorna a sessão atual e atualiza lastActivityAt.
 */
export function getSession(): CoreSession | null {
  if (!currentSession) return null;
  currentSession = touchSession(currentSession);
  return currentSession;
}

/**
 * Retorna o sessionId do core (para uso no restante do pixel).
 */
export function getCoreSessionId(): string | null {
  return currentSession?.sessionId ?? null;
}

/**
 * Retorna o visitorId do core.
 */
export function getCoreVisitorId(): string | null {
  return currentVisitor?.visitorId ?? null;
}

/**
 * Estado atual da página (scroll, duração) para enriquecer outros eventos.
 */
export function getPageViewState() {
  return getCurrentPageViewState();
}
